import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute, CustomerRoute, BusinessOwnerRoute, PractitionerRoute, CenterAdminRoute } from "@/components/ProtectedRoute";
import { DashboardAccessRoute } from "@/components/dashboard/DashboardAccessRoute";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { AppLayout } from "@/components/app/AppLayout";

// Public Pages - Critical for initial load
import Index from "./pages/Index";
import Browse from "./pages/Browse";
import ProviderDetail from "./pages/ProviderDetail";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy loaded public pages
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Help = lazy(() => import("./pages/Help"));
const HelpArticle = lazy(() => import("./pages/HelpArticle"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const LocationPage = lazy(() => import("./pages/locations/LocationPage"));
const VerifyEmailPending = lazy(() => import("./pages/VerifyEmailPending"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const BusinessPublicProfile = lazy(() => import("./pages/business/BusinessPublicProfile"));
const Install = lazy(() => import("./pages/Install"));
const Offline = lazy(() => import("./pages/Offline"));

// Lazy loaded App Pages (PWA mobile-first)
const AppHome = lazy(() => import("./pages/app/AppHome"));
const AppSearch = lazy(() => import("./pages/app/AppSearch"));
const AppBookings = lazy(() => import("./pages/app/AppBookings"));
const AppMessages = lazy(() => import("./pages/app/AppMessages"));
const AppProfile = lazy(() => import("./pages/app/AppProfile"));

// Lazy loaded Customer Pages
const CustomerBookings = lazy(() => import("./pages/customer/CustomerBookings"));
const CustomerPurchases = lazy(() => import("./pages/customer/CustomerPurchases"));
const CustomerSettings = lazy(() => import("./pages/customer/CustomerSettings"));
const CustomerPayments = lazy(() => import("./pages/customer/CustomerPayments"));

// Lazy loaded Practitioner Pages
const PractitionerSchedule = lazy(() => import("./pages/practitioner/PractitionerSchedule"));

// Lazy loaded Business Dashboard Pages
const DashboardOverview = lazy(() => import("./pages/dashboard/DashboardOverview"));
const DashboardBookings = lazy(() => import("./pages/dashboard/DashboardBookings"));
const DashboardPractitioners = lazy(() => import("./pages/dashboard/DashboardPractitioners"));
const DashboardServices = lazy(() => import("./pages/dashboard/DashboardServices"));
const DashboardInventory = lazy(() => import("./pages/dashboard/DashboardInventory"));
const DashboardReports = lazy(() => import("./pages/dashboard/DashboardReports"));
const ReportsTotalRevenue = lazy(() => import("./pages/dashboard/ReportsTotalRevenue"));
const ReportsServiceRevenue = lazy(() => import("./pages/dashboard/ReportsServiceRevenue"));
const ReportsProductSales = lazy(() => import("./pages/dashboard/ReportsProductSales"));
const ReportsCustomerSales = lazy(() => import("./pages/dashboard/ReportsCustomerSales"));
const DashboardSubscription = lazy(() => import("./pages/dashboard/DashboardSubscription"));
const DashboardBusinessProfile = lazy(() => import("./pages/dashboard/DashboardBusinessProfile"));
const DashboardSettings = lazy(() => import("./pages/dashboard/DashboardSettings"));
const DashboardPayments = lazy(() => import("./pages/dashboard/DashboardPayments"));
const DashboardCustomers = lazy(() => import("./pages/dashboard/DashboardCustomers"));
const DashboardStaff = lazy(() => import("./pages/dashboard/DashboardStaff"));
const DashboardPOS = lazy(() => import("./pages/dashboard/DashboardPOS"));
const SubscriptionPaymentPage = lazy(() => import("./pages/payment/SubscriptionPaymentPage"));
const BookingCheckout = lazy(() => import("./pages/BookingCheckout"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));

// Lazy loaded Admin Pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminBusinesses = lazy(() => import("./pages/admin/AdminBusinesses"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminCampaigns = lazy(() => import("./pages/admin/AdminCampaigns"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AcceptAdminInvite = lazy(() => import("./pages/AcceptAdminInvite"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      gcTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      retry: 1, // Only retry once on failure
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CookieConsent />
          <PWAUpdatePrompt />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <main id="main-content" className="min-h-screen">
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
              <Route path="/browse" element={<Browse />} />
              <Route path="/provider/:id" element={<ProviderDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/login" element={<Auth />} />
              <Route path="/register" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/wellness/:location" element={<LocationPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/:slug" element={<HelpArticle />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/payment/subscription" element={<SubscriptionPaymentPage />} />
              <Route path="/booking/checkout" element={<BookingCheckout />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/install" element={<Install />} />
              <Route path="/offline" element={<Offline />} />
              
              {/* Public Business Profile - SEO-friendly URL */}
              <Route path="/b/:slug" element={<BusinessPublicProfile />} />

              {/* App Routes - Mobile-first PWA experience */}
              <Route path="/app" element={<CustomerRoute><AppLayout><AppHome /></AppLayout></CustomerRoute>} />
              <Route path="/app/search" element={<CustomerRoute><AppLayout><AppSearch /></AppLayout></CustomerRoute>} />
              <Route path="/app/bookings" element={<CustomerRoute><AppLayout><AppBookings /></AppLayout></CustomerRoute>} />
              <Route path="/app/messages" element={<CustomerRoute><AppLayout><AppMessages /></AppLayout></CustomerRoute>} />
              <Route path="/app/profile" element={<CustomerRoute><AppLayout><AppProfile /></AppLayout></CustomerRoute>} />

              {/* Customer Routes */}
              <Route path="/customer/bookings" element={<CustomerRoute><CustomerBookings /></CustomerRoute>} />
              <Route path="/customer/purchases" element={<CustomerRoute><CustomerPurchases /></CustomerRoute>} />
              <Route path="/customer/settings" element={<CustomerRoute><CustomerSettings /></CustomerRoute>} />
              <Route path="/customer/payments" element={<CustomerRoute><CustomerPayments /></CustomerRoute>} />

              {/* Practitioner Routes (employees of premium centers) */}
              <Route path="/practitioner/schedule" element={<PractitionerRoute><PractitionerSchedule /></PractitionerRoute>} />

              {/* Business Dashboard Routes - Role-based access control */}
              <Route path="/dashboard" element={<DashboardAccessRoute level="staff"><DashboardOverview /></DashboardAccessRoute>} />
              <Route path="/dashboard/pos" element={<DashboardAccessRoute level="staff"><DashboardPOS /></DashboardAccessRoute>} />
              <Route path="/dashboard/bookings" element={<DashboardAccessRoute level="staff"><DashboardBookings /></DashboardAccessRoute>} />
              <Route path="/dashboard/customers" element={<DashboardAccessRoute level="staff"><DashboardCustomers /></DashboardAccessRoute>} />
              <Route path="/dashboard/settings" element={<DashboardAccessRoute level="staff"><DashboardSettings /></DashboardAccessRoute>} />
              
              {/* Manager-level access */}
              <Route path="/dashboard/services" element={<DashboardAccessRoute level="manager"><DashboardServices /></DashboardAccessRoute>} />
              <Route path="/dashboard/inventory" element={<DashboardAccessRoute level="manager"><DashboardInventory /></DashboardAccessRoute>} />
              <Route path="/dashboard/practitioners" element={<DashboardAccessRoute level="manager"><DashboardPractitioners /></DashboardAccessRoute>} />
              <Route path="/dashboard/reports" element={<DashboardAccessRoute level="manager"><DashboardReports /></DashboardAccessRoute>} />
              <Route path="/dashboard/reports/total-revenue" element={<DashboardAccessRoute level="manager"><ReportsTotalRevenue /></DashboardAccessRoute>} />
              <Route path="/dashboard/reports/service-revenue" element={<DashboardAccessRoute level="manager"><ReportsServiceRevenue /></DashboardAccessRoute>} />
              <Route path="/dashboard/reports/product-sales" element={<DashboardAccessRoute level="manager"><ReportsProductSales /></DashboardAccessRoute>} />
              <Route path="/dashboard/reports/customer-sales" element={<DashboardAccessRoute level="manager"><ReportsCustomerSales /></DashboardAccessRoute>} />
              <Route path="/dashboard/payments" element={<DashboardAccessRoute level="manager"><DashboardPayments /></DashboardAccessRoute>} />
              
              {/* Owner-only access */}
              <Route path="/dashboard/subscription" element={<DashboardAccessRoute level="owner"><DashboardSubscription /></DashboardAccessRoute>} />
              <Route path="/dashboard/profile" element={<DashboardAccessRoute level="owner"><DashboardBusinessProfile /></DashboardAccessRoute>} />
              <Route path="/dashboard/staff" element={<DashboardAccessRoute level="owner"><DashboardStaff /></DashboardAccessRoute>} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminProtectedRoute><AdminOverview /></AdminProtectedRoute>} />
              <Route path="/admin/businesses" element={<AdminProtectedRoute><AdminBusinesses /></AdminProtectedRoute>} />
              <Route path="/admin/subscriptions" element={<AdminProtectedRoute><AdminSubscriptions /></AdminProtectedRoute>} />
              <Route path="/admin/customers" element={<AdminProtectedRoute><AdminCustomers /></AdminProtectedRoute>} />
              <Route path="/admin/campaigns" element={<AdminProtectedRoute><AdminCampaigns /></AdminProtectedRoute>} />
              <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
              
              {/* Admin Invite Accept (public route) */}
              <Route path="/accept-admin-invite" element={<AcceptAdminInvite />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
